/**
 * Screen 3: Kanban Pipeline — /pipeline
 *
 * Uses @dnd-kit for drag-and-drop stage changes.
 * Donor pattern: Atomic CRM (marmelab) kanban board.
 *
 * UX rules (TZ 9.4, 9.5):
 * - Pipeline split into 3 tabs: «Воронка» (sales), «Внедрение» (delivery), «Поддержка» (account)
 * - Tab state persisted in localStorage (key: stars_pipeline_tab), default: sales
 * - new_lead is shown in «Воронка» tab as first column (also visible in /leads)
 * - Drag to move deal to next stage
 * - Server-side gate check fires on drop; 409 gate_blocked → show toast, revert
 * - Terminal stages (lost, churned) are not shown in any tab
 *
 * Gate blocking on drop (TZ 3.5):
 * - If server returns 409 gate_blocked → optimistic update reverted
 * - NO override on frontend
 */

import { useState, useCallback } from 'react'
import { Link } from '@tanstack/react-router'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { AlertCircle } from 'lucide-react'
import { useDealsList, usePipeline, useMoveStage } from '@/hooks/useDeals'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { formatRub, formatRelativeTime } from '@/lib/utils'
import type { Deal, PipelineStage, StageKey, StagePhase } from '@/types'

// Заголовок страницы по фазе (вкладки вынесены в левое меню как отдельные роуты)
const PHASE_TITLES: Record<StagePhase, string> = {
  sales: 'Воронка продаж',
  delivery: 'Внедрение',
  account: 'Поддержка',
  terminal: 'Архив',
}

// All non-terminal stages to fetch from API
const ALL_KANBAN_STAGES: StageKey[] = [
  // sales (including new_lead)
  'new_lead',
  'conversation',
  'proposal_sent',
  'won',
  // delivery
  'onboarding',
  'build',
  'integration',
  'testing',
  'launch',
  'hypercare',
  // account
  'active',
  'upsell',
  'renewal',
  'at_risk',
]

// Phase color classes for column headers
const PHASE_COLORS: Record<string, string> = {
  sales:    'bg-blue-50 border-blue-200',
  delivery: 'bg-purple-50 border-purple-200',
  account:  'bg-green-50 border-green-200',
}

const PHASE_LABEL_COLORS: Record<string, string> = {
  sales:    'text-blue-700',
  delivery: 'text-purple-700',
  account:  'text-green-700',
}

// ---- Deal card in column ----

function DealCard({ deal, isDragging }: { deal: Deal; isDragging?: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: deal.id,
    data: { deal },
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Link to="/contact/$id" params={{ id: deal.id }}>
        <Card className="p-3 mb-2 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow border bg-white">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {deal.contact?.display_name ?? deal.title}
              </p>
              {deal.contact?.business_niche && (
                <p className="text-xs text-muted-foreground mt-0.5 truncate">
                  {deal.contact.business_niche}
                </p>
              )}
            </div>
            {deal.is_repeat && (
              <Badge variant="repeat" className="shrink-0 text-[10px]">повт.</Badge>
            )}
          </div>

          <div className="mt-2 flex items-center justify-between gap-2 text-xs text-muted-foreground">
            {deal.value ? (
              <span className="font-medium text-foreground">{formatRub(deal.value)}</span>
            ) : (
              <span>—</span>
            )}
            <span>{formatRelativeTime(deal.updated_at)}</span>
          </div>

          {/* Show gate warning if any gate is pending */}
          {deal.gates?.some((g) => g.result === 'pending') && (
            <div className="mt-2 flex items-center gap-1 text-[10px] text-amber-600">
              <AlertCircle className="h-3 w-3" />
              Гейт не пройден
            </div>
          )}
        </Card>
      </Link>
    </div>
  )
}

// ---- Kanban column ----

function KanbanColumn({
  stage,
  deals,
}: {
  stage: PipelineStage
  deals: Deal[]
}) {
  return (
    <div className={`flex-shrink-0 w-60 rounded-lg border ${PHASE_COLORS[stage.phase] ?? 'bg-gray-50'} p-3`}>
      {/* Column header */}
      <div className="mb-3">
        <span className={`text-[10px] font-semibold uppercase tracking-wide ${PHASE_LABEL_COLORS[stage.phase] ?? 'text-gray-500'}`}>
          {stage.phase}
        </span>
        <h3 className="text-sm font-semibold text-foreground mt-0.5 leading-tight">{stage.name}</h3>
        <span className="text-xs text-muted-foreground">{deals.length} сделок</span>
      </div>

      {/* Drop zone */}
      <SortableContext
        items={deals.map((d) => d.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="min-h-[80px]">
          {deals.map((deal) => (
            <DealCard key={deal.id} deal={deal} />
          ))}
          {deals.length === 0 && (
            <div className="h-16 rounded border-2 border-dashed border-gray-200 flex items-center justify-center">
              <span className="text-xs text-muted-foreground">Перетащите сделку</span>
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  )
}

// ---- Gate error toast (simplified) ----

function GateErrorBanner({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 shadow-lg">
      <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
      <div>
        <p className="text-sm font-medium text-red-700">Переход заблокирован</p>
        <p className="text-xs text-red-600">{message}</p>
      </div>
      <button onClick={onDismiss} className="ml-2 text-xs text-red-500 hover:text-red-700">✕</button>
    </div>
  )
}

// ---- Main component ----

export function PipelinePage({ phase = 'sales' }: { phase?: StagePhase } = {}) {
  const { data: pipeline } = usePipeline()
  const { data: allDeals = [] } = useDealsList(ALL_KANBAN_STAGES)
  const moveStage = useMoveStage()

  const [activeId, setActiveId] = useState<string | null>(null)
  const [gateError, setGateError] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  )

  // Stages for this phase, sorted by sort field
  const activePhase = phase
  const tabStages = (pipeline?.stages ?? [])
    .filter((s) => s.phase === activePhase && ALL_KANBAN_STAGES.includes(s.key))
    .sort((a, b) => a.sort - b.sort)

  // Group deals by stage (open only)
  const dealsByStage = useCallback(
    (stageKey: StageKey): Deal[] =>
      allDeals.filter((d) => d.stage_key === stageKey && d.status === 'open'),
    [allDeals]
  )

  // Total open deals in active tab
  const tabDealsCount = tabStages.reduce(
    (sum, s) => sum + dealsByStage(s.key).length,
    0
  )

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id as string)
    setGateError(null)
  }

  async function handleDragEnd(event: DragEndEvent) {
    setActiveId(null)
    const { active, over } = event
    if (!over || active.id === over.id) return

    const deal = allDeals.find((d) => d.id === active.id)
    if (!deal) return

    // over.id might be a column (stageKey) or another deal id
    // Find target stage: if over.id is a deal, use its stage_key; otherwise use it as stageKey
    const targetDeal = allDeals.find((d) => d.id === over.id)
    const targetStageKey = (targetDeal ? targetDeal.stage_key : over.id) as StageKey

    if (targetStageKey === deal.stage_key) return

    // Optimistic UI: update will be reverted if 409 returned
    try {
      await moveStage.mutateAsync({ dealId: deal.id, stageKey: targetStageKey })
    } catch (err: unknown) {
      // Check for gate_blocked (409)
      const apiErr = (err as { apiError?: { error: string; gate?: string } }).apiError
      if (apiErr?.error === 'gate_blocked') {
        setGateError(
          `Гейт: ${apiErr.gate ?? 'неизвестно'} — выполните условия перехода`
        )
      } else if (apiErr?.error === 'transition_not_allowed') {
        setGateError('Переход не разрешён между этими стадиями')
      } else {
        setGateError('Ошибка при смене стадии')
      }
    }
  }

  const activeDeal = activeId ? allDeals.find((d) => d.id === activeId) : null

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">{PHASE_TITLES[activePhase]}</h1>
          <p className="text-sm text-muted-foreground">
            {tabDealsCount} {activePhase === 'sales' ? 'активных сделок' : 'сделок'}
            {activePhase === 'sales' && (
              <>
                {' · Лиды также доступны в '}
                <Link to="/leads" className="underline">Ленте лидов</Link>
              </>
            )}
          </p>
        </div>
      </div>

      {/* Kanban board */}
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4">
          {tabStages.map((stage) => (
            <KanbanColumn
              key={stage.key}
              stage={stage}
              deals={dealsByStage(stage.key)}
            />
          ))}
          {tabStages.length === 0 && (
            <p className="text-sm text-muted-foreground py-8">
              Нет стадий для этой фазы
            </p>
          )}
        </div>

        {/* Drag overlay */}
        <DragOverlay>
          {activeDeal && <DealCard deal={activeDeal} isDragging />}
        </DragOverlay>
      </DndContext>

      {/* Gate error feedback */}
      {gateError && (
        <GateErrorBanner message={gateError} onDismiss={() => setGateError(null)} />
      )}
    </div>
  )
}
